import { FEEDBACK_NEGATIVE_VALUE, FEEDBACK_POSITIVE_VALUE, useUserFeedback } from "@/hooks/use-user-feedback";
import styled from "@emotion/styled";
import { ThumbsDownIcon, ThumbsUpIcon } from "lucide-react";
import type { ReactNode } from "react";
import { colors, radius, spacing } from "../../design-tokens";
import { AnimatedSection } from "../../primitives/animated-section";
import { ParagraphSmall } from "../../primitives/typographies";
import { CommentOptions } from "./comment-options";
import { FreeTextInput } from "./free-text-input";

const FeedbackContent = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  width: fit-content;
  color: ${colors.text};
`;

const FeedbackGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.lg}px;
`;

const FeedbackLabel = styled(ParagraphSmall)`
  color: ${colors.text};
`;

const FeedbackButtons = styled.div`
  display: flex;
  gap: ${spacing.sm}px;
  align-items: center;
  flex-shrink: 0;
`;

const FeedbackButton = styled.button<{ isActive?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: inherit;
  padding: ${spacing.xs}px;
  border: 1px solid transparent;
  background: transparent;
  color: ${props => (props.isActive ? colors.primary : colors.text)};
  cursor: pointer;
  border-radius: ${radius.sm}px;
  transition: all 200ms ease-in-out;

  svg {
    stroke-width: ${props => (props.isActive ? "2" : "1.5")};
    transition: stroke-width 200ms ease-in-out;
  }

  &:hover:not(:disabled) {
    color: ${colors.primary};

    svg {
      stroke-width: 2;
    }
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const FeedbackContainer = styled.div`
  max-width: 480px;
`;

type UserFeedbackComponentProps = {
  readonly traceId?: string;
  readonly observationId?: string;
  readonly children?: ReactNode;
};

export function UserFeedbackComponent({ traceId, observationId, children }: UserFeedbackComponentProps) {
  const {
    feedback,
    isSubmitting,
    selectedComment,
    freeTextComment,
    shouldShowComments,
    shouldShowTextArea,
    isPositive,
    isVisible,
    canSubmitFeedback,
    handleUserFeedback,
    handleCommentSelection,
    handleFreeTextSubmit,
    setFreeTextComment,
    handleBackToComments,
  } = useUserFeedback({ traceId, observationId });

  return (
    <FeedbackContainer>
      <AnimatedSection isVisible={isVisible} animationType="fadeSlide">
        <FeedbackContent id="user-feedback">
          <FeedbackGroup>
            <FeedbackLabel>Did we answer your question?</FeedbackLabel>
            <FeedbackButtons>
              <FeedbackButton
                isActive={feedback === FEEDBACK_POSITIVE_VALUE}
                onClick={() => handleUserFeedback(FEEDBACK_POSITIVE_VALUE)}
                aria-label="Mark as helpful"
                disabled={isSubmitting || !canSubmitFeedback}
              >
                <ThumbsUpIcon size={14} />
              </FeedbackButton>
              <FeedbackButton
                isActive={feedback === FEEDBACK_NEGATIVE_VALUE}
                onClick={() => handleUserFeedback(FEEDBACK_NEGATIVE_VALUE)}
                aria-label="Mark as not helpful"
                disabled={isSubmitting || !canSubmitFeedback}
              >
                <ThumbsDownIcon size={14} />
              </FeedbackButton>
            </FeedbackButtons>
          </FeedbackGroup>
          {children}
        </FeedbackContent>

        <AnimatedSection isVisible={shouldShowComments} animationType="fadeSlide">
          <div style={{ marginTop: spacing.md }} />
          <CommentOptions
            selectedComment={selectedComment}
            isSubmitting={isSubmitting}
            isPositive={isPositive}
            onCommentSelect={handleCommentSelection}
          />
        </AnimatedSection>

        <AnimatedSection isVisible={shouldShowTextArea} animationType="fadeSlide">
          <div style={{ marginTop: spacing.md }} />
          <FreeTextInput
            isSubmitting={isSubmitting}
            value={freeTextComment}
            onChange={setFreeTextComment}
            onBack={handleBackToComments}
            onSubmit={handleFreeTextSubmit}
            showBack={isPositive}
          />
        </AnimatedSection>
      </AnimatedSection>
    </FeedbackContainer>
  );
}
